import React from 'react';
import { Icon } from '../../design/Icon';
import { Alert } from '../../components/shared/Feedback';
import { useTerms } from '../../layout/ShellStatus';
import {
  CALL_VOLUMES,
  HISTORY_DAYS,
  IMPORT_STALL_MINUTES,
  INDUSTRIES,
  STEPS,
  TIME_ZONES,
  activityLines,
  checkTone,
  checksSummary,
  importView,
  possessive,
  routingTypeLabel,
} from './onboardingLogic';

export function StepFrame({ index, title, sub, children, actions, onBack }) {
  return (
    <>
      <div className="ob-card-head">
        <div className="eyebrow">
          Step {index + 1} of {STEPS.length}
        </div>
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      <div className="ob-card-body">{children}</div>
      <div className="ob-card-foot">
        {index > 0 && (
          <button className="btn" type="button" onClick={onBack}>
            <Icon name="chevL" />
            Back
          </button>
        )}
        <span className="grow" />
        {actions}
      </div>
    </>
  );
}

function FieldError({ message }) {
  if (!message) return null;
  return (
    <span className="field-error" role="alert">
      <Icon name="alert" className="ico-sm" />
      <span>{message}</span>
    </span>
  );
}

export function OrganizationStep({ form, onChange, errors, busy, onContinue }) {
  const set = (key) => (event) => onChange({ ...form, [key]: key === 'estimatedMonthlyCalls' ? Number(event.target.value) : event.target.value });
  return (
    <StepFrame
      index={0}
      title="Tell us about your organization"
      sub="We use this for your workspace name, report headers, and the time zone your dashboards display."
      actions={
        <button className="btn btn-primary" type="button" onClick={onContinue} disabled={busy}>
          Continue
          <Icon name="chevR" />
        </button>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="o1">
            Organization name
          </label>
          <input className={`input${errors.companyName ? ' invalid' : ''}`} id="o1" value={form.companyName} onChange={set('companyName')} autoComplete="organization" />
          <FieldError message={errors.companyName} />
        </div>
        <div className="field">
          <label className="label" htmlFor="o2">
            Industry
          </label>
          <select className="select" id="o2" value={form.industry} onChange={set('industry')}>
            {INDUSTRIES.map((industry) => (
              <option key={industry}>{industry}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="o3">
            Primary contact
          </label>
          <input className="input" id="o3" value={form.contactName} onChange={set('contactName')} autoComplete="name" />
        </div>
        <div className="field">
          <label className="label" htmlFor="o4">
            Work email
          </label>
          <input className={`input${errors.email ? ' invalid' : ''}`} id="o4" type="email" value={form.email} onChange={set('email')} autoComplete="email" />
          <FieldError message={errors.email} />
        </div>
        <div className="field">
          <label className="label" htmlFor="o5">
            Time zone for dashboards
          </label>
          <select className="select" id="o5" value={form.timeZone} onChange={set('timeZone')}>
            {TIME_ZONES.map((zone) => (
              <option key={zone.value} value={zone.value}>
                {zone.label}
              </option>
            ))}
          </select>
          <span className="hint">Call records are stored in UTC and shown in this time zone.</span>
        </div>
        <div className="field">
          <label className="label" htmlFor="o6">
            Expected calls per month
          </label>
          <select className="select" id="o6" value={form.estimatedMonthlyCalls} onChange={set('estimatedMonthlyCalls')}>
            {CALL_VOLUMES.map((band) => (
              <option key={band.value} value={band.value}>
                {band.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </StepFrame>
  );
}

export function ConnectStep({ orgName, method, onMethod, error, upn, domain, onUseAnotherAccount, busy, onBack, onContinue }) {
  return (
    <StepFrame
      index={1}
      title={`Connect ${possessive(orgName)} Microsoft 365 tenant`}
      sub="Vertolytics reads Teams Phone call records through Microsoft Graph. Choose how your administrator wants to connect."
      onBack={onBack}
      actions={
        <button className="btn btn-primary" type="button" onClick={onContinue} disabled={busy}>
          Continue to Microsoft
          <Icon name="external" />
        </button>
      }
    >
      {error && (
        <Alert
          tone="error"
          title={error.title}
          actions={
            <button className="btn btn-sm" type="button" onClick={onUseAnotherAccount}>
              Use another account
            </button>
          }
        >
          {error.body}
        </Alert>
      )}
      <div className="choice">
        <label>
          <input type="radio" name="m" checked={method === 'consent'} onChange={() => onMethod('consent')} />
          <span>
            <span className="strong" style={{ display: 'block' }}>
              Approve with an administrator account
            </span>
            <span className="small sub">A Global Administrator signs in and approves read-only access. Takes about two minutes.</span>
            <span className="badge b-info" style={{ marginTop: 8 }}>
              Recommended
            </span>
          </span>
        </label>
        <label>
          <input type="radio" name="m" checked={method === 'app'} onChange={() => onMethod('app')} />
          <span>
            <span className="strong" style={{ display: 'block' }}>
              Use your own app registration
            </span>
            <span className="small sub">
              For IT teams that manage their own Entra app. You enter the client ID and a client secret; the tenant ID comes from your sign-in, and the secret is stored in Azure Key Vault.
            </span>
          </span>
        </label>
      </div>
      {upn && (
        <div className="panel small">
          <div className="row" style={{ gap: 8 }}>
            <Icon name="building" />
            <span>
              Detected tenant <strong>{domain}</strong> from {upn}
            </span>
          </div>
        </div>
      )}
    </StepFrame>
  );
}

const PERMISSIONS = [
  ['CallRecords.Read.All', 'Read call records', 'Call times, durations, routes through auto attendants and queues, and call quality. Powers every dashboard.'],
  ['CallRecord-PstnCalls.Read.All', 'Read the PSTN call log', 'Monthly count of public phone network calls.'],
  ['Directory.Read.All', 'Read directory data', 'Matches calls to agents by name and department, and confirms that the person setting up is a Microsoft 365 administrator.'],
  ['Presence.Read.All', 'Read presence', 'Shows who is available, on a call, or away on the wallboard.'],
  ['CallRecordings.Read.All', 'Stream call recordings', 'Plays recordings in call detail and sends audio for transcription. Audio is processed in temporary storage and deleted; Vertolytics never keeps recordings.'],
  ['CallTranscripts.Read.All', 'Read Teams transcripts', "Uses Teams' own transcripts when your calling policy produces them."],
];

export function ApproveStep({ orgName, method, error, credentials, onCredentials, credentialError, tenantId, busy, onBack, onApprove, onCopyLink, onSaveCredentials }) {
  const ownApp = method === 'app';
  return (
    <StepFrame
      index={2}
      title="Approve read-only access"
      sub="Microsoft will show these permissions on the next screen. Vertolytics can't change Teams settings, place calls, or send messages."
      onBack={onBack}
      actions={
        ownApp ? (
          <button className="btn btn-primary" type="button" onClick={onSaveCredentials} disabled={busy}>
            Save and check the connection
            <Icon name="chevR" />
          </button>
        ) : (
          <button className="btn btn-primary" type="button" onClick={onApprove} disabled={busy}>
            <Icon name="external" />
            Approve in Microsoft
          </button>
        )
      }
    >
      {error && (
        <Alert
          tone="error"
          title={error.title}
          actions={
            <>
              <button className="btn btn-sm btn-primary" type="button" onClick={onApprove} disabled={busy}>
                Try again
              </button>
              <button className="btn btn-sm" type="button" onClick={onCopyLink} disabled={busy}>
                Copy approval link
              </button>
            </>
          }
        >
          {error.body}
        </Alert>
      )}
      <div>
        {PERMISSIONS.map(([scope, title, body]) => (
          <div className="perm" key={scope}>
            <span className="ci ok" style={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center' }}>
              <Icon name="lock" className="ico-sm" />
            </span>
            <div>
              <div className="strong small">{title}</div>
              <div className="small sub">{body}</div>
            </div>
            <code>{scope}</code>
          </div>
        ))}
      </div>
      <Alert icon="shield">
        Recordings and transcripts: some states require every party's consent to record a call. Check the rules where you operate; {possessive(orgName)} Teams announcement should mention recording and automated transcription before calls connect.
      </Alert>
      {ownApp && (
        <div className="form-grid">
          <div className="field">
            <label className="label" htmlFor="a0">
              Tenant ID
            </label>
            <input className="input" id="a0" value={tenantId || ''} readOnly />
            <span className="hint">From your sign-in; it can't be changed here.</span>
          </div>
          <div className="field">
            <label className="label" htmlFor="a1">
              Client ID
            </label>
            <input className="input" id="a1" value={credentials.clientId} onChange={(e) => onCredentials({ ...credentials, clientId: e.target.value })} autoComplete="off" />
          </div>
          <div className="field">
            <label className="label" htmlFor="a2">
              Client secret
            </label>
            <input className="input" id="a2" type="password" value={credentials.clientSecret} onChange={(e) => onCredentials({ ...credentials, clientSecret: e.target.value })} autoComplete="off" />
            <span className="hint">Stored in Azure Key Vault. Vertolytics never shows it again.</span>
            <FieldError message={credentialError} />
          </div>
        </div>
      )}
    </StepFrame>
  );
}

const PENDING_CHECKS = ['Tenant reachable', 'Consent granted', 'Read call records', 'Read PSTN call log', 'Recordings and transcripts', 'Presence'];

export function CheckStep({ orgName, shortName, result, running, requestError, onUseAnotherAccount, onBack, onRunAgain, onContinue }) {
  const summary = result ? checksSummary(result, orgName) : null;
  const canContinue = Boolean(result?.isValid) && !running;
  return (
    <StepFrame
      index={3}
      title="Checking the connection"
      sub={`Vertolytics tests each permission against ${possessive(shortName)} tenant. You can continue with warnings; failures must be fixed first.`}
      onBack={onBack}
      actions={
        <button className="btn btn-primary" type="button" onClick={onContinue} disabled={!canContinue}>
          Continue
          <Icon name="chevR" />
        </button>
      }
    >
      {requestError && !running && (
        <Alert
          tone="error"
          title={requestError.title}
          actions={
            requestError.adminRequired ? (
              <button className="btn btn-sm" type="button" onClick={onUseAnotherAccount}>
                Use another account
              </button>
            ) : (
              <button className="btn btn-sm btn-primary" type="button" onClick={onRunAgain}>
                <Icon name="refresh" />
                Run checks again
              </button>
            )
          }
        >
          {requestError.body}
        </Alert>
      )}
      {summary && !running && !requestError && (
        <Alert
          tone={summary.tone}
          title={summary.title}
          actions={
            summary.tone === 'error' ? (
              <button className="btn btn-sm btn-primary" type="button" onClick={onRunAgain}>
                <Icon name="refresh" />
                Run checks again
              </button>
            ) : undefined
          }
        >
          {summary.body}
        </Alert>
      )}
      <div aria-busy={running || undefined}>
        {running || !result
          ? PENDING_CHECKS.map((title) => (
              <div className="check-row" key={title}>
                <span className="ci run">
                  <span className="spinner" />
                </span>
                <div>
                  <div className="strong small">{title}</div>
                  <div className="small sub">Checking…</div>
                </div>
                <span className="badge">Running</span>
              </div>
            ))
          : result.permissions.map((check) => {
              const tone = checkTone(check.level);
              return (
                <div className="check-row" key={check.permissionName}>
                  <span className={`ci ${tone.ci}`}>
                    <Icon name={tone.icon} />
                  </span>
                  <div>
                    <div className="strong small">{check.dataType}</div>
                    <div className="small sub">{check.detail}</div>
                  </div>
                  <span className={`badge ${tone.badge}`}>{tone.label}</span>
                </div>
              );
            })}
      </div>
    </StepFrame>
  );
}

export function OrganizeStep({
  orgName,
  levelName,
  onLevelName,
  rows,
  divisions,
  communities,
  csvError,
  busy,
  loadError,
  fileInputRef,
  onFile,
  onRow,
  divisionOf,
  onDownloadErrors,
  onBack,
  onSave,
}) {
  const terms = useTerms();
  const autoAttendants = rows.filter((r) => r.mapping.routingEntityType === 'AutoAttendant').length;
  const queues = rows.length - autoAttendants;
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const topName = levelName.trim() || 'Division';
  return (
    <StepFrame
      index={4}
      title={`Organize calls by ${topName} and ${terms.communityLower}`}
      sub={`We found ${plural(autoAttendants, 'auto attendant', 'auto attendants')} and ${plural(queues, 'call queue', 'call queues')}. Name the levels of ${possessive(orgName)} hierarchy, then confirm where each one's calls belong. You can change all of this later.`}
      onBack={onBack}
      actions={
        <button className="btn btn-primary" type="button" onClick={onSave} disabled={busy}>
          Save and import history
          <Icon name="chevR" />
        </button>
      }
    >
      {loadError && <Alert tone="error" title="Mappings couldn't load">{loadError}</Alert>}
      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="lv1">
            Top level name
          </label>
          <input className="input" id="lv1" value={levelName} onChange={(e) => onLevelName(e.target.value)} />
          <span className="hint">Examples: Division, Region, Office</span>
        </div>
        <div className="field">
          <label className="label" htmlFor="lv2">
            Lowest level name
          </label>
          <input className="input" id="lv2" value={terms.community} readOnly />
          <span className="hint">Calls are attributed to this level.</span>
        </div>
      </div>
      <div className="row between">
        <div className="strong small">Suggested mappings</div>
        <div className="row">
          <input ref={fileInputRef} className="sr-file" type="file" accept=".csv,text/csv" onChange={onFile} tabIndex={-1} aria-hidden="true" />
          <button className="btn btn-sm" type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
            <Icon name="upload" className="ico-sm" />
            Upload {terms.communityLower} list (CSV)
          </button>
        </div>
      </div>
      <div className="table-wrap" style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
        <table className="table compact">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name in Teams</th>
              <th>{topName}</th>
              <th>{terms.community}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const choices = row.divisionId ? communities.filter((c) => c.hierarchyNodeId === row.divisionId || c.id === row.communityId) : communities;
              return (
                <tr key={row.mapping.id}>
                  <td className="small muted">{routingTypeLabel(row.mapping.routingEntityType)}</td>
                  <td className="cell-title">{row.mapping.routingEntityName}</td>
                  <td>
                    <select
                      className="select"
                      style={{ height: 30 }}
                      aria-label={`${topName} for ${row.mapping.routingEntityName}`}
                      value={row.divisionId}
                      onChange={(e) => onRow(index, { divisionId: e.target.value, communityId: '' })}
                    >
                      <option value="">All {lowerPlural(topName)}</option>
                      {divisions.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="select"
                      style={{ height: 30 }}
                      aria-label={`${terms.community} for ${row.mapping.routingEntityName}`}
                      value={row.communityId}
                      onChange={(e) => {
                        const community = communities.find((c) => c.id === e.target.value);
                        onRow(index, { communityId: e.target.value, divisionId: community?.hierarchyNodeId ? divisionOf(community.hierarchyNodeId) : row.divisionId });
                      }}
                    >
                      <option value="">From caller&apos;s words</option>
                      {choices.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{row.nameMatch && <span className="badge b-info">Name match</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Alert>
        When a queue serves many {terms.communitiesLower}, Vertolytics identifies the {terms.communityLower} from what the caller says (with a quoted line from the transcript as evidence). Upload {possessive(orgName)} {terms.communityLower} directory to improve matching.
      </Alert>
      {csvError && (
        <Alert
          tone="error"
          title={csvError.title}
          actions={
            <button className="btn btn-sm" type="button" onClick={onDownloadErrors}>
              Download error report
            </button>
          }
        >
          {csvError.body}
        </Alert>
      )}
    </StepFrame>
  );
}

function lowerPlural(levelName) {
  const lower = levelName.toLowerCase();
  return lower.endsWith('s') ? lower : `${lower}s`;
}

export function ImportStep({ progress, loadError, resuming, resumeError, onResume, onBack, onContinue }) {
  const view = importView(progress);
  const lines = activityLines(progress);
  const warning = (progress?.warnings || [])[0];
  const failure = (progress?.errors || [])[0];
  const resumeButton = (
    <button className="btn btn-sm btn-primary" type="button" onClick={onResume} disabled={resuming}>
      <Icon name="refresh" />
      {resuming ? 'Resuming…' : 'Resume import'}
    </button>
  );
  return (
    <StepFrame
      index={5}
      title={`Importing the last ${HISTORY_DAYS} days`}
      sub="Dashboards fill in as data arrives. You can leave this page; the import keeps running, and this page shows its progress when you come back."
      onBack={onBack}
      actions={
        view.done ? (
          <button className="btn btn-primary" type="button" onClick={onContinue}>
            Continue
            <Icon name="chevR" />
          </button>
        ) : (
          <button className="btn" type="button" onClick={onContinue}>
            Continue in background
          </button>
        )
      }
    >
      {loadError && <Alert tone="error" title="Progress couldn't load">{loadError}</Alert>}
      {view.throttled && warning && (
        <Alert tone="warning" title="Microsoft is limiting requests">
          {warning.userFriendlyMessage}
        </Alert>
      )}
      {resumeError && (
        <Alert tone="error" title={resumeError.title}>
          {resumeError.body}
        </Alert>
      )}
      {view.failed && (failure || view.resume) && (
        <Alert tone="error" title="The import stopped" actions={view.resume && resumeButton}>
          {failure ? failure.userFriendlyMessage || failure.message : 'The import stopped before it finished.'}{' '}
          {view.resume && 'Resume continues from the first day not yet imported; calls already imported are kept.'}
        </Alert>
      )}
      {view.resume === 'stalled' && (
        <Alert tone="error" title="The import isn't making progress" actions={resumeButton}>
          Nothing has changed for {IMPORT_STALL_MINUTES} minutes or more, which usually means the server running it restarted. Resume continues from the first day not yet
          imported; calls already imported are kept.
        </Alert>
      )}
      <div className="row" style={{ gap: 16 }}>
        <div className="grow">
          <div className="row between small">
            <strong>Overall progress</strong>
            <span className="num">{view.overall}%</span>
          </div>
          <div className="progress" style={{ marginTop: 8 }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={view.overall} aria-label="Overall progress">
            <span style={{ width: `${view.overall}%` }} />
          </div>
        </div>
        <div className="caption" style={{ width: 150, textAlign: 'right' }}>
          {view.done ? 'Done' : view.failed ? 'Stopped' : view.resume === 'stalled' ? 'No progress' : view.minutesLeft ? `About ${view.minutesLeft} minutes left` : 'Estimating…'}
        </div>
      </div>
      <div>
        {view.rows.map((row) => (
          <div className="imp-row" key={row.title}>
            <div>
              <div className="strong small">{row.title}</div>
              <div className="caption">{row.caption}</div>
            </div>
            <div className="progress">
              <span style={{ width: `${row.pct}%`, ...(row.paused ? { background: 'var(--warning-mark)' } : row.pct >= 100 ? { background: 'var(--good-mark)' } : {}) }} />
            </div>
            <div className="small num" style={{ textAlign: 'right' }}>
              {row.pct >= 100 && !row.paused && (
                <>
                  <span style={{ color: 'var(--good)' }}>
                    <Icon name="check" className="ico-sm" />
                  </span>{' '}
                </>
              )}
              {row.value}
            </div>
          </div>
        ))}
      </div>
      <div>
        <div className="strong small" style={{ marginBottom: 6 }}>
          Activity
        </div>
        <div className="log" aria-live="polite">
          {lines.length === 0 ? 'Starting the import…' : lines.map((line) => (
            <React.Fragment key={line.text}>
              {line.warning ? <span style={{ color: 'var(--warning)' }}>{line.text}</span> : line.text}
              <br />
            </React.Fragment>
          ))}
        </div>
      </div>
    </StepFrame>
  );
}

export function FinishStep({ orgName, shortName, kpis, invites, onInvites, inviteError, busy, onBack, onSetRoles, onOpenOverview }) {
  const terms = useTerms();
  return (
    <StepFrame
      index={6}
      title={`${orgName} is ready`}
      sub="Dashboards are live. Transcription and analysis will finish in the background."
      onBack={onBack}
      actions={
        <>
          <button className="btn" type="button" onClick={onSetRoles} disabled={busy}>
            Set roles first
          </button>
          <button className="btn btn-primary" type="button" onClick={onOpenOverview} disabled={busy}>
            Open the Overview
            <Icon name="chevR" />
          </button>
        </>
      }
    >
      <div className="kpis" style={{ boxShadow: 'none' }}>
        {kpis.map((kpi) => (
          <div className="kpi" key={kpi.label}>
            <div className="k-label">{kpi.label}</div>
            <div className="k-value">{kpi.value}</div>
            <div className="k-meta">{kpi.meta}</div>
          </div>
        ))}
      </div>
      <div className="field">
        <label className="label" htmlFor="inv">
          Invite teammates
        </label>
        <textarea className="textarea" id="inv" rows={3} value={invites} onChange={(e) => onInvites(e.target.value)} placeholder="name@yourcompany.com, another@yourcompany.com" />
        {inviteError ? (
          <FieldError message={inviteError} />
        ) : (
          <span className="hint">
            They&apos;ll sign in with their {shortName} Microsoft account. Choose roles and {terms.communityLower} access in Users and roles.
          </span>
        )}
      </div>
    </StepFrame>
  );
}
